import {json, redirect, type LoaderArgs} from '@shopify/remix-oxygen';
import {AnalyticsPageType} from '@shopify/hydrogen';
import {routeHeaders} from '~/data/cache';

//domains
import {
  useDefaultAdsQuery,
  PRODUCT_PER_PAGE,
  PRODUCT_PER_PAGE_WITHOUT_ADS,
} from 'domains/collections';
import {
  parseNativeFilterParams,
  legacyBoostParamsToNative,
  sortToNative,
  resolveOffsetCursor,
  countProductsByScan,
  deriveTotalProducts,
  extractPriceBounds,
  nativeProductToBfs,
  ALL_PRODUCTS_COLLECTION_HANDLE,
  type NativeFilter,
  type NativeProductNode,
  NATIVE_COLLECTION_PAGE_QUERY,
  NATIVE_FACETS_QUERY,
  fetchTypeGroupedWindow,
  FILTER_PARAM_PREFIX,
} from 'domains/nativeCollection';
import {fetchSearchFacet} from 'domains/nativeSearch';
import {
  MODEL_FACET_ID,
  modelsForVendor,
  selectedBrandModel,
} from '~/lib/brandModels';
import {canonicalUrl, seoPayload} from '~/lib/seo.server';
import {BRAND_COLLECTIONS} from '~/lib/brandCollections';
import type {Collection as CollectionType} from '@shopify/hydrogen/storefront-api-types';

//translation
import translation from 'translations/collection.json';
import tagTranslation from 'translations/tags.json';
import {routeLocale} from 'domains/global';

//components
import Container from 'components/Container';
import CollectionMain from 'components/collection/CollectionMain';

export const headers = routeHeaders;

/**
 * Brand PLP: /collections/vendor?pf_v_brand=<VENDOR>. The pf_v_brand param
 * is this route's identity (every brand link on the site and any indexed
 * URLs use it), so it is kept verbatim; filtering runs natively against the
 * hidden all-products collection with the vendor(s) as base ProductFilters.
 */

const PLP_CACHE_CONTROL =
  'public, max-age=1, s-maxage=45, stale-while-revalidate=600';

interface NativePageData {
  collection: {
    id: string;
    products: {
      filters: NativeFilter[];
      pageInfo: {hasNextPage: boolean; endCursor: string | null};
      nodes: NativeProductNode[];
    };
  } | null;
}

type DefaultAds = Awaited<ReturnType<typeof useDefaultAdsQuery>>;

/** Same 120s per-instance cache idea as the collection route's chrome. */
const ADS_TTL_MS = 120_000;
let defaultAdsCache: {expires: number; promise: Promise<DefaultAds>} | null =
  null;

function getDefaultAds(): Promise<DefaultAds> {
  if (defaultAdsCache && defaultAdsCache.expires > Date.now()) {
    return defaultAdsCache.promise;
  }
  const promise = useDefaultAdsQuery();
  promise.catch(() => {
    defaultAdsCache = null;
  });
  defaultAdsCache = {expires: Date.now() + ADS_TTL_MS, promise};
  return promise;
}

export async function loader({request, context}: LoaderArgs) {
  const url = new URL(request.url);
  const searchParams = url.searchParams;

  // The desktop mega-menu historically linked ?q=<brand> (Boost treated q
  // as a free-text search term) — map any surviving links/bookmarks onto
  // the vendor identity param.
  if (searchParams.has('q')) {
    const next = new URLSearchParams(searchParams);
    const brands = next.getAll('q').filter(Boolean);
    next.delete('q');
    for (const brand of brands) next.append('pf_v_brand', brand);
    const qs = next.toString();
    return redirect(`${url.pathname}${qs ? `?${qs}` : ''}`, 301);
  }

  // Old Boost filter params from indexed links → native params; the
  // pf_v_brand identity param passes through untouched.
  const legacyParams = legacyBoostParamsToNative(searchParams, {
    keep: ['pf_v_brand'],
  });
  if (legacyParams) {
    const qs = legacyParams.toString();
    return redirect(`${url.pathname}${qs ? `?${qs}` : ''}`, 301);
  }

  const requestedVendors = searchParams
    .getAll('pf_v_brand')
    .filter((v) => v && v !== 'null' && v !== 'undefined');

  // A brand with its own collection lives at /collections/<handle>; this
  // page would be a second, identical URL for it. Filters, sort and page
  // carry over (both routes read the same native params).
  const brandHandle =
    requestedVendors.length === 1
      ? BRAND_COLLECTIONS[requestedVendors[0]]
      : undefined;
  if (brandHandle) {
    const next = new URLSearchParams(searchParams);
    next.delete('pf_v_brand');
    const qs = next.toString();
    const path = url.pathname.replace(
      /\/collections\/vendor\/?$/,
      `/collections/${brandHandle}`,
    );
    return redirect(`${path}${qs ? `?${qs}` : ''}`, 301);
  }

  const {country, language} = context.storefront.i18n;

  const defaultAdsData = await getDefaultAds();
  const defaultAdvertisement =
    defaultAdsData.defaultAdvertisement.productSettings.product_settings_main;
  const hasAds = Boolean(defaultAdvertisement?.advertisement?.ad_is_active);
  const productPerPage = hasAds
    ? PRODUCT_PER_PAGE
    : PRODUCT_PER_PAGE_WITHOUT_ADS;

  const parsed = parseNativeFilterParams(searchParams);
  const filters = [
    ...requestedVendors.map((vendor) => ({productVendor: vendor})),
    ...parsed.filters,
  ];
  const {sortKey, reverse} = sortToNative(parsed.sort);
  const offset = (parsed.page - 1) * productPerPage;

  const queryArgs = {
    storefront: context.storefront,
    handle: ALL_PRODUCTS_COLLECTION_HANDLE,
    filters,
    sortKey,
    reverse,
    country,
    language,
  };

  // Default ordering groups by product type, newest-listed first within
  // each type — a brand page stops interleaving helmets with gloves and
  // oil. An explicit ?sort= choice overrides the grouping.
  // parseNativeFilterParams defaults sort to the string 'manual', so test
  // the raw param — only an explicit ?sort= choice opts out of grouping.
  const grouped = !searchParams.has('sort')
    ? await fetchTypeGroupedWindow({
        storefront: context.storefront,
        handle: ALL_PRODUCTS_COLLECTION_HANDLE,
        filters,
        offset,
        first: productPerPage,
        country,
        language,
      })
    : null;

  const {after, outOfRange} = grouped
    ? {after: null, outOfRange: grouped.outOfRange}
    : await resolveOffsetCursor({
        ...queryArgs,
        offset,
      });

  if (outOfRange && parsed.page > 1) {
    throw new Response(null, {status: 404});
  }

  const [pageData, priceBoundsData, searchModelFacet] = await Promise.all([
    grouped
      ? Promise.resolve(null)
      : context.storefront.query<NativePageData>(NATIVE_COLLECTION_PAGE_QUERY, {
          variables: {
            handle: ALL_PRODUCTS_COLLECTION_HANDLE,
            filters,
            sortKey,
            reverse,
            first: productPerPage,
            after,
            country,
            language,
          },
        }),
    parsed.hasPriceFilter
      ? context.storefront.query<NativePageData>(NATIVE_FACETS_QUERY, {
          variables: {
            handle: ALL_PRODUCTS_COLLECTION_HANDLE,
            filters: filters.filter((f) => !('price' in f)),
            country,
            language,
          },
        })
      : Promise.resolve(null),
    // 型號 is the one facet here that outgrew the Storefront API's 100-value
    // ceiling on a collection's facets, so its rows come from the search
    // connection, which scopes values to the result set. One brand at a time:
    // a two-brand page has no single name to search, and no model band either.
    requestedVendors.length === 1
      ? fetchSearchFacet({
          env: context.env,
          q: requestedVendors[0],
          facetId: MODEL_FACET_ID,
          filters,
          country,
          language,
        })
      : Promise.resolve(null),
  ]);

  if (!grouped && !pageData?.collection) {
    // The hidden all-products collection is this route's data source — if it
    // was unpublished/deleted, fail loudly (503 = temporary, keeps crawlers
    // from deindexing brand pages) instead of caching an empty catalog.
    throw new Response('all-products collection unavailable', {status: 503});
  }

  const productsConnection = pageData?.collection?.products;
  const collectionFacets = grouped
    ? grouped.facets
    : productsConnection?.filters ?? [];

  let totalProducts = grouped
    ? grouped.totalProducts
    : deriveTotalProducts(collectionFacets, filters);
  if (totalProducts === null) {
    totalProducts = await countProductsByScan(queryArgs);
  }

  // Trust the search facet's counts only when the search matched exactly the
  // products this page renders. It reaches them by brand name, not by the
  // vendor filter alone, so a brand with a product that never names it would
  // report short — and short counts on rows a shopper taps are worse than the
  // capped list this replaces.
  const facets =
    searchModelFacet && searchModelFacet.totalCount === totalProducts
      ? collectionFacets.some((facet) => facet.id === MODEL_FACET_ID)
        ? collectionFacets.map((facet) =>
            facet.id === MODEL_FACET_ID ? searchModelFacet.facet : facet,
          )
        : [...collectionFacets, searchModelFacet.facet]
      : collectionFacets;

  const priceBounds = extractPriceBounds(
    priceBoundsData?.collection?.products?.filters ?? facets,
  );

  const products = (
    grouped ? grouped.nodes : outOfRange ? [] : productsConnection?.nodes ?? []
  ).map(nativeProductToBfs);

  const selectedLocale =
    context.storefront.i18n.language.toLowerCase() as routeLocale;

  const vendorTitle = requestedVendors.join(' & ');

  // 型號 intro band (single-brand pages only): the facet itself comes from
  // Search & Discovery (custom.model metafield); the registry supplies the
  // selected model's copy and banner.
  const brandModels =
    requestedVendors.length === 1 ? modelsForVendor(requestedVendors[0]) : [];
  const brandModel = selectedBrandModel(brandModels, searchParams);

  // pf_v_brand is this route's identity, not a facet: /collections/vendor is
  // one URL per brand. Dropping it from the canonical (the root loader's
  // query-stripped default) pointed all ~170 brand pages at the bare
  // /collections/vendor index, telling Google they were one duplicated page.
  // Keep it; collapse only the re-cuts — facets and sorts — onto the brand's
  // own URL, and let ?page=N self-canonicalise like the collection route.
  const isFacetedOrSorted =
    searchParams.has('sort') ||
    Array.from(searchParams.keys()).some((k) =>
      k.startsWith(FILTER_PARAM_PREFIX),
    );
  const canonical = canonicalUrl(request.url, [
    'pf_v_brand',
    ...(!isFacetedOrSorted && parsed.page > 1 ? ['page'] : []),
  ]);

  const seoTitle = vendorTitle
    ? language === 'EN'
      ? `${vendorTitle} Products`
      : `${vendorTitle} 產品系列`
    : 'Search Brands';
  // An empty description let Hydrogen's Seo reducer fall through to the
  // shop-wide root copy, so every brand page described the whole store. A
  // single selected model contributes its own registry copy.
  const modelIntro = brandModel
    ? language === 'EN'
      ? brandModel.intro_en
      : brandModel.intro_zh
    : '';
  const seoDescription = !vendorTitle
    ? ''
    : language === 'EN'
    ? brandModel
      ? `${vendorTitle} ${brandModel.name} at Helmet King. ${modelIntro}`.trim()
      : `Browse every ${vendorTitle} product at Helmet King — helmets, riding gear and accessories, filterable by model, type and size.`
    : brandModel
    ? `頭盔王 ${vendorTitle} ${brandModel.name}。${modelIntro}`.trim()
    : `瀏覽頭盔王 ${vendorTitle} 全線產品 — 頭盔、騎行裝備及配件，可按型號、類型及尺碼篩選。`;

  // Brand PLPs listed products but emitted no CollectionPage/ItemList/
  // BreadcrumbList, so the same grid was structured data on
  // /collections/<handle> and nothing here. seoPayload.collection reads only
  // title/description/image/products, so a synthetic collection carrying this
  // route's own copy and product nodes gives brand pages the same markup.
  // titleTemplate is stripped to keep the site's '%s | <root>' title format.
  const {titleTemplate: _vendorTitleTemplate, ...vendorSeo} =
    seoPayload.collection({
      collection: {
        title: seoTitle,
        description: seoDescription,
        seo: {title: seoTitle, description: seoDescription},
        products: {
          nodes: grouped
            ? grouped.nodes
            : outOfRange
            ? []
            : productsConnection?.nodes ?? [],
        },
      } as unknown as CollectionType,
      url: canonical,
    });

  // A brand with nothing live renders a 200 page with an empty grid — a soft
  // 404, and now a self-canonicalising one. Don't 404 it: a brand can be
  // temporarily sold out and the URL should survive that. Withhold indexing
  // until it has something to show, keeping follow so the sidebar links are
  // still crawled.
  const seo = {
    ...vendorSeo,
    url: canonical,
    ...(totalProducts === 0 ? {robots: {noIndex: true, noFollow: false}} : {}),
  };

  return json(
    {
      analytics: {
        pageType: AnalyticsPageType.page,
      },
      translation: translation[selectedLocale],
      tagTranslation: tagTranslation[selectedLocale],
      hasAds,
      seo,
      defaultAdvertisement,
      products,
      brandModel,
      nativeFilters: {
        facets,
        priceBounds,
      },
      totalProducts,
    },
    {
      headers: {
        'Cache-Control': PLP_CACHE_CONTROL,
      },
    },
  );
}

export default function Collection() {
  return (
    <section className="lg:pt-10 relative">
      <Container>
        <CollectionMain type="brands" />
      </Container>
    </section>
  );
}
